import { Component } from '@angular/core';
import { SideNavService } from '../../side-nav.service';
import { ISideNavItem } from '../../side-nav.interface';
import { HttpService } from '@services/http.service';

@Component({
  selector: 'app-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.css'],
  standalone: false,
})
export class SideNavComponent {
  currentLang?: string;

  constructor(
    public sideNavService: SideNavService,
    private http: HttpService,
  ) {
    this.sideNavService.items = [
      {
        /** t(sideNav.items.dashboard) */
        name: 'sideNav.items.dashboard',
        icon: 'dashboard',
        link: '/dashboard',
      },
      {
        /** t(sideNav.items.user) */
        name: 'sideNav.items.user',
        icon: 'account_circle',
        link: '/user',
      },
      {
        /** t(sideNav.headers.tools) */
        name: 'sideNav.headers.tools',
        header: true,
      },
      {
        /** t(sideNav.items.kohoEnterpriseSearch) */
        name: 'sideNav.items.kohoEnterpriseSearch',
        icon: 'search',
        link: '/kohosearch',
      },
      {
        /** t(sideNav.items.memos) */
        name: 'sideNav.items.memos',
        icon: 'export_notes',
        link: '/memos',
      },
      {
        /** t(sideNav.items.kohodocuments) */
        name: 'sideNav.items.kohodocuments',
        icon: 'content_paste',
        link: '/kohodocuments',
      },
      {
        /** t(sideNav.items.requests) */
        name: 'sideNav.items.requests',
        icon: 'support_agent',
        link: '/requests',
      },
      {
        /** t(sideNav.items.triggers) */
        name: 'sideNav.items.triggers',
        icon: 'play_arrow',
        link: '/triggers',
      },
      {
        /** t(sideNav.items.tasks) */
        name: 'sideNav.items.tasks',
        icon: 'list_alt',
        link: '/tasks',
        onload: (item: ISideNavItem) => {
          // Update once onload (but only after 5 seconds to avoid initial loading delay)
          setTimeout(() => this.updateQueuedTasks(item), 5000);

          // Update once every 5 minutes
          setInterval(() => this.updateQueuedTasks(item), 300000);
        },
      },
      {
        /** t(sideNav.items.signdocuments) */
        name: 'sideNav.items.signdocuments',
        icon: 'drive_file_rename_outline',
        link: '/sign',
      },

      {
        /** t(sideNav.items.processModels) */
        name: 'sideNav.items.processModels',
        icon: 'account_tree',
        link: '/process-models',
        permission: 'process-models',
      },
      {
        /** t(sideNav.items.kohoprices) */
        name: 'sideNav.items.kohoprices',
        icon: 'euro_symbol',
        link: '/kohoprices',
        permission: 'kohoprices',
      },
      {
        /** t(sideNav.items.announcements) */
        name: 'sideNav.items.announcements',
        icon: 'announcement',
        link: '/announcements',
        permission: 'announcements',
      },
      {
        /** t(sideNav.items.pulse) */
        name: 'sideNav.items.pulse',
        icon: 'monitor_heart',
        link: '/pulse',
      },
      {
        /** t(sideNav.headers.monitoring) */
        name: 'sideNav.headers.monitoring',
        header: true,
        permission: ['emails', 'executions', 'smsmessages', 'executions-errors', 'teamsmessages'],
      },
      {
        /** t(sideNav.items.executions) */
        name: 'sideNav.items.executions',
        icon: 'autorenew',
        link: '/executions',
        permission: 'executions',
      },
      {
        /** t(sideNav.items.emails) */
        name: 'sideNav.items.emails',
        icon: 'mail',
        link: '/emails',
        permission: 'emails',
      },
      {
        /** t(sideNav.items.sms-messages) */
        name: 'sideNav.items.sms-messages',
        icon: 'sms',
        link: '/sms-messages',
        permission: 'sms-messages',
      },
      {
        /** t(sideNav.items.botmessages) */
        name: 'sideNav.items.botmessages',
        icon: 'smart_toy',
        link: '/botmessages',
        permission: 'botmessages',
      },
      {
        /** t(sideNav.items.teamsmessages) */
        name: 'sideNav.items.teamsmessages',
        icon: 'groups',
        link: '/teamsmessages',
        permission: 'teamsmessages',
      },
      {
        /** t(sideNav.items.executionsErrors) */
        name: 'sideNav.items.executionsErrors',
        icon: 'error',
        link: '/executions-errors',
        permission: 'executions-errors',
        onload: (item: ISideNavItem) => {
          setTimeout(() => this.updateExecutionsErrors(item), 7500);

          // Update once every 10 minutes
          setInterval(() => this.updateExecutionsErrors(item), 600_000);
        },
      },
      {
        /** t(sideNav.headers.automationManagement) */
        name: 'sideNav.headers.automationManagement',
        header: true,
        permission: ['clients', 'secrets', 'systems', 'queues', 'resource-locks', 'settings'],
      },
      {
        /** t(sideNav.items.clients) */
        name: 'sideNav.items.clients',
        icon: 'star',
        link: '/clients',
        permission: 'clients',
      },
      {
        /** t(sideNav.items.settings) */
        name: 'sideNav.items.settings',
        icon: 'settings',
        link: '/settings',
        permission: 'settings',
      },
      {
        /** t(sideNav.items.secrets) */
        name: 'sideNav.items.secrets',
        icon: 'vpn_key',
        link: '/secrets',
        permission: 'secrets',
      },
      {
        /** t(sideNav.items.systems) */
        name: 'sideNav.items.systems',
        icon: 'api',
        link: '/systems',
        permission: 'systems',
      },
      {
        /** t(sideNav.items.queues) */
        name: 'sideNav.items.queues',
        icon: 'category',
        link: '/queues',
        permission: 'queues',
      },
      {
        /** t(sideNav.items.resourceLocks) */
        name: 'sideNav.items.resourceLocks',
        icon: 'lock',
        link: '/resource-locks',
        permission: 'resource-locks',
      },
      {
        /** t(sideNav.headers.admin) */
        name: 'sideNav.headers.admin',
        header: true,
        permission: 'admin',
      },
      {
        /** t(sideNav.items.users) */
        name: 'sideNav.items.users',
        icon: 'account_circle',
        link: '/admin/users',
        permission: 'admin',
      },
      {
        /** t(sideNav.items.usergroups) */
        name: 'sideNav.items.usergroups',
        icon: 'groups',
        link: '/admin/usergroups',
        permission: 'admin',
      },
      {
        /** t(sideNav.items.gitrepositories) */
        name: 'sideNav.items.gitrepositories',
        icon: 'code',
        link: '/admin/gitrepositories',
        permission: 'admin',
      },
      {
        /** t(sideNav.items.savedForms) */
        name: 'sideNav.items.savedForms',
        icon: 'folder_special',
        link: '/saved-forms',
        permission: 'admin',
      },
      {
        /** t(sideNav.items.flowlogs) */
        name: 'sideNav.items.flowlogs',
        icon: 'article',
        link: '/admin/logs',
        permission: 'admin',
      },
      {
        /** t(sideNav.items.corelogs) */
        name: 'sideNav.items.corelogs',
        icon: 'assignment',
        link: '/admin/corelogs',
        permission: 'admin',
      },
    ];
  }

  updateQueuedTasks(item: ISideNavItem) {
    this.http
      .get('/tasks/queued')
      .then((response: { count: number }) => {
        const count = response.count > 25 ? '25+' : response.count;
        item.badge = count || null;
      })
      .catch((error) => {
        console.log(error);
      });
  }

  updateExecutionsErrors(item: ISideNavItem) {
    if (item.visible) {
      this.http
        .get('/executions-errors/unresolved')
        .then((response: { count: number }) => {
          const count = response.count > 25 ? '25+' : response.count;
          item.badge = count || null;
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }
}
